package org.teddapp.project.ui.focus

import org.teddapp.project.ui.TeddSpace

import org.teddapp.project.ui.TeddCard

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import org.teddapp.project.ui.ReorderableColumn
import org.teddapp.project.ui.rememberReorderableState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import org.teddapp.project.ui.nav.NavIntent
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.TeddBottomSheet
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.core.LocalCoreSession
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.review.ReviewView
import org.teddapp.project.ui.review.rememberReviewModel
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.pomodoro.isoAddDays
import org.teddapp.project.ui.settings.SettingsScreen

/**
 * Écran Focus unique. La logique vit dans [FocusModel] ; la mise en page
 * s'adapte à la largeur :
 *
 * - compacte : pile verticale, navigation en bas, réglages et détail en feuilles ;
 * - moyenne : rail de navigation, centre et panneau minuteur + détail ;
 * - étendue : rail, centre plus large, panneau minuteur + détail.
 *
 * Les tâches entrent dans le jour par la saisie rapide, la reprise d'une session passée,
 * ou le lancement d'une micro depuis une compétence ; il n'y a plus de panneau de sources.
 */
@Composable
fun FocusScreen(
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val model = rememberFocusModel(repos)
    // Chaque course terminée devient un fait dans le journal du noyau.
    model.coreSession = LocalCoreSession.current
    // Venu de Compétences pour une étape de parcours : la course demandée démarre dès que
    // la session du jour est ouverte.
    val sessionReady = model.currentSession != null
    LaunchedEffect(sessionReady) {
        if (sessionReady) NavIntent.consumeFocusLaunch()?.let { model.launchTask(it.task, it.minutes, it.skillId) }
    }
    WithWindowClass(modifier.fillMaxSize()) { wc ->
        when (wc) {
            WindowClass.Compact -> FocusCompactLayout(model, repos, onNavigate)
            WindowClass.Medium, WindowClass.Expanded -> FocusExpandedLayout(model, repos, onNavigate)
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Compact
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun FocusCompactLayout(model: FocusModel, repos: TeddRepositories, onNavigate: (Section) -> Unit) {
    val scope = rememberCoroutineScope()
    var settingsOpen by remember { mutableStateOf(false) }
    var inspectorItemId by remember { mutableStateOf<String?>(null) }
    val session = model.currentSession

    Box(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
        Column(modifier = Modifier.fillMaxSize().imePadding()) {
            FocusCompactHeader(timerMinutes = model.timerMinutes, onOpenSettings = { settingsOpen = true })
            Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp)) {
                FocusTabs(tab = model.tab, onChange = { model.tab = it })
            }
            when (model.tab) {
                FocusTab.Today -> {
                    if (session == null) {
                        Box(modifier = Modifier.weight(1f).fillMaxWidth(), contentAlignment = Alignment.Center) {
                            Text("Ouverture de la session…", color = TeddTokens.InkMuted, fontSize = 12.sp)
                        }
                    } else {
                        // Le minuteur et la progression s'effacent quand on fait défiler la liste du jour.
                        org.teddapp.project.ui.TeddCollapsingLayout(
                            modifier = Modifier.weight(1f).fillMaxWidth(),
                            header = {
                                Column {
                                    FocusTimerCard(model = model, expanded = false)
                                    session.date_end?.let { end ->
                                        FocusExtensionBanner(session.date_start, end, onCancel = { model.setExtension(null) }, compact = true)
                                    }
                                    FocusProgressBlock(model = model, compact = true, onExtend = null)
                                }
                            },
                        ) {
                        Column(modifier = Modifier.fillMaxSize()) {
                        FocusQuickAdd(model = model, compact = true)
                        if (model.sortedItems.isEmpty()) {
                            FocusEmptyToday(modifier = Modifier.weight(1f))
                        } else {
                            // Appui long sur une carte pour la classer parmi celles de même priorité.
                            val reorder = rememberReorderableState()
                            ReorderableColumn(
                                items = model.sortedItems,
                                keyOf = { it.id },
                                state = reorder,
                                onReorder = { from, to -> model.moveItem(from, to) },
                                modifier = Modifier.weight(1f).fillMaxWidth().verticalScroll(rememberScrollState()).padding(horizontal = 12.dp, vertical = 8.dp),
                            ) { index, item, handle ->
                                val checks by repos.pomodoroChecks.observeByItem(item.id).collectAsState(emptyList())
                                val locked = reorder.recentlyDropped
                                val list = model.sortedItems
                                Box(modifier = Modifier.fillMaxWidth().padding(bottom = 4.dp).then(handle)) {
                                    FocusCompactItemRow(
                                        onMoveUp = if (canMoveWithinGroup(list, index, -1)) ({ model.moveItem(index, index - 1) }) else null,
                                        onMoveDown = if (canMoveWithinGroup(list, index, +1)) ({ model.moveItem(index, index + 1) }) else null,
                                        item = item,
                                        meta = model.resolveMeta(item),
                                        verifsDone = checks.count { it.checked != 0L },
                                        verifsTotal = checks.size,
                                        onToggle = { if (!locked) model.toggleItem(item) },
                                        onOpenInspector = { if (!locked) inspectorItemId = item.id },
                                    )
                                }
                            }
                        }
                        }
                        }
                    }
                }
                FocusTab.History -> FocusCompactHistory(
                    sessions = model.history,
                    repos = repos,
                    resolveMeta = model.resolveMeta,
                    modifier = Modifier.weight(1f),
                    onClone = { model.cloneSession(it) },
                )
                FocusTab.Review -> ReviewView(
                    model = rememberReviewModel(repos),
                    compact = true,
                    modifier = Modifier.weight(1f),
                )
            }
            TeddBottomNav(active = Section.Focus, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
        }

        model.editingQuick?.let { q ->
            QuickEditDialog(item = q, onClose = { model.editingQuick = null }, onSave = { t, m -> model.saveQuickEdit(t, m) })
        }
        FocusSnackbar(model.snackbar, bottomPadding = 100.dp)
        FocusRunEnd(model)
        if (model.bdnfOpen) {
            BdnfPauseDialog(
                onDone = { exercise, seconds, efficiency -> model.recordBdnf(exercise, seconds, efficiency) },
                onDismiss = { model.bdnfOpen = false },
            )
        }

        TeddBottomSheet(visible = settingsOpen, onDismiss = { settingsOpen = false }) {
            FocusSettingsSheet(
                currentMinutes = model.timerMinutes,
                hasExtension = model.hasExtension,
                canTerminate = model.canTerminate,
                onPickMinutes = { model.pickTimerMinutes(it) },
                onTerminate = { model.terminateToday(); settingsOpen = false },
                onExtend3Days = { model.setExtension(isoAddDays(org.teddapp.project.ui.pomodoro.POM_TODAY_ISO, 3), "Session étendue (+3 jours)"); settingsOpen = false },
                onCancelExtension = { model.setExtension(null); settingsOpen = false },
                onClose = { settingsOpen = false },
            )
        }
        val inspectorItem = inspectorItemId?.let { id -> model.items.firstOrNull { it.id == id } }
        TeddBottomSheet(visible = inspectorItem != null, onDismiss = { inspectorItemId = null }) {
            if (inspectorItem != null) {
                val checks by repos.pomodoroChecks.observeByItem(inspectorItem.id).collectAsState(emptyList())
                FocusInspectorSheet(
                    item = inspectorItem,
                    meta = model.resolveMeta(inspectorItem),
                    checks = checks,
                    onToggleCheck = { id, checked -> scope.launch { repos.pomodoroChecks.setChecked(id, checked) } },
                    onAddCheck = { label ->
                        scope.launch {
                            val next = (checks.maxOfOrNull { it.order_index } ?: -1L) + 1L
                            repos.pomodoroChecks.create(inspectorItem.id, label, orderIndex = next)
                        }
                    },
                    onRemoveCheck = { id -> scope.launch { repos.pomodoroChecks.softDelete(id) } },
                    onChangePriority = { p -> model.setPriority(inspectorItem, p) },
                    onRemove = { model.removeItem(inspectorItem); inspectorItemId = null },
                    onClose = { inspectorItemId = null },
                    onEdit = { inspectorItemId = null; model.openQuickEdit(inspectorItem) },
                )
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Étendu (et moyen)
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun FocusExpandedLayout(
    model: FocusModel,
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit,
) {
    var showSettings by remember { mutableStateOf(false) }
    var showExtend by remember { mutableStateOf(false) }
    var settingsDrawerOpen by remember { mutableStateOf(false) }
    val session = model.currentSession

    Box(modifier = Modifier.fillMaxSize()) {
        Row(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
            TeddNavRail(active = Section.Focus, onNavigate = onNavigate, onSettingsClick = { settingsDrawerOpen = true })
            Column(modifier = Modifier.weight(1f).fillMaxHeight()) {
                if (session == null) {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text("Ouverture de la session…", color = TeddTokens.InkMuted, fontSize = 12.sp)
                    }
                } else {
                    FocusExpandedHeader(
                        session = session,
                        tab = model.tab,
                        onTab = { model.tab = it },
                        onToggleSettings = { showSettings = !showSettings },
                    )
                    session.date_end?.let { end ->
                        FocusExtensionBanner(session.date_start, end, onCancel = { model.setExtension(null) }, compact = false)
                    }
                    if (showSettings) {
                        Row(
                            modifier = Modifier.fillMaxWidth().background(TeddTokens.Surface)
                                .padding(horizontal = 24.dp, vertical = 10.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            FocusDurationChips(current = model.timerMinutes, onPick = { model.pickTimerMinutes(it); showSettings = false })
                        }
                    }
                    if (model.tab == FocusTab.Today) {
                        // Le centre prend toute la largeur jusqu'au chronomètre : un grand écran
                        // sert à voir plus, pas à laisser un tiers de vide.
                        Column(modifier = Modifier.weight(1f)) {
                            FocusProgressBlock(model = model, compact = false, onExtend = { showExtend = true })
                            FocusQuickAdd(model = model, compact = false)
                            FocusExpandedItemsList(
                                items = model.sortedItems,
                                resolveMeta = model.resolveMeta,
                                selectedId = model.selectedItemId,
                                onSelect = { model.toggleSelect(it) },
                                onToggle = { model.toggleItem(it) },
                                onStartTimer = { model.startTimer(it.id) },
                                onSetPriority = { item, p -> model.setPriority(item, p) },
                                onRemove = { model.removeItem(it) },
                                onEdit = { model.openQuickEdit(it) },
                                repos = repos,
                                onMove = { from, to -> model.moveItem(from, to) },
                            )
                        }
                    } else if (model.tab == FocusTab.Review) {
                        ReviewView(
                            model = rememberReviewModel(repos),
                            compact = false,
                            modifier = Modifier.weight(1f),
                        )
                    } else {
                        HistoryView(
                            repos = repos,
                            sessions = model.history,
                            resolveMeta = model.resolveMeta,
                            onClone = { model.cloneSession(it) },
                        )
                    }
                }
            }
            FocusRightPanel(model = model, repos = repos)
        }

        FocusRunEnd(model)
        if (model.bdnfOpen) {
            BdnfPauseDialog(
                onDone = { exercise, seconds, efficiency -> model.recordBdnf(exercise, seconds, efficiency) },
                onDismiss = { model.bdnfOpen = false },
            )
        }

        // Tiroir des réglages généraux
        AnimatedVisibility(visible = settingsDrawerOpen, enter = fadeIn(tween(180)), exit = fadeOut(tween(180))) {
            Box(
                modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.32f))
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = { settingsDrawerOpen = false }),
            )
        }
        AnimatedVisibility(
            visible = settingsDrawerOpen,
            enter = slideInHorizontally(tween(280)) { it },
            exit = slideOutHorizontally(tween(220)) { it },
            modifier = Modifier.align(Alignment.CenterEnd),
        ) { SettingsScreen(onClose = { settingsDrawerOpen = false }, onOpenBackup = { settingsDrawerOpen = false; onNavigate(Section.Backup) }) }

        // Extension de session
        AnimatedVisibility(visible = showExtend, enter = fadeIn(tween(160)), exit = fadeOut(tween(140))) {
            session?.let { s ->
                ExtendDialog(
                    initialEndDate = s.date_end ?: isoAddDays(s.date_start, 3),
                    onClose = { showExtend = false },
                    onSave = { end -> model.setExtension(end, "Session étendue"); showExtend = false },
                    onCancelExtension = if (s.date_end != null) { { model.setExtension(null); showExtend = false } } else null,
                )
            }
        }

        model.editingQuick?.let { q ->
            QuickEditDialog(item = q, onClose = { model.editingQuick = null }, onSave = { t, m -> model.saveQuickEdit(t, m) })
        }
        FocusSnackbar(model.snackbar, bottomPadding = 18.dp)
    }
}

/** La colonne du minuteur : 360 dp par défaut, de 300 à 520. */
private val FOCUS_PANE = org.teddapp.project.ui.PaneSpec("focus.timer", default = 360.dp, min = 300.dp, max = 520.dp)

/** Colonne de droite : minuteur, puis détail de la tâche sélectionnée. */
@Composable
private fun FocusRightPanel(model: FocusModel, repos: TeddRepositories) {
    val scope = rememberCoroutineScope()
    org.teddapp.project.ui.TeddSidePane(
        spec = FOCUS_PANE, side = org.teddapp.project.ui.PaneSide.Right, title = "le minuteur", icon = "clock",
        contentPadding = PaddingValues(start = TeddSpace.S2, top = TeddSpace.S5, end = TeddSpace.S6, bottom = TeddSpace.S5),
    ) {
    Column(
        modifier = Modifier.fillMaxSize(),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S4),
    ) {
        TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(TeddSpace.S6)) {
            FocusTimerCard(model = model, expanded = true)
        }
        val selected = model.selectedItem
        if (selected != null) {
            val checks by repos.pomodoroChecks.observeByItem(selected.id).collectAsState(emptyList())
            Box(modifier = Modifier.weight(1f)) {
                ItemInspector(
                    item = selected,
                    meta = model.resolveMeta(selected),
                    checks = checks,
                    onToggleCheck = { id, checked -> scope.launch { repos.pomodoroChecks.setChecked(id, checked) } },
                    onAddCheck = { label ->
                        scope.launch {
                            val next = (checks.maxOfOrNull { it.order_index } ?: -1L) + 1L
                            repos.pomodoroChecks.create(selected.id, label, orderIndex = next)
                        }
                    },
                    onRemoveCheck = { id -> scope.launch { repos.pomodoroChecks.softDelete(id) } },
                    onEdit = { model.openQuickEdit(selected) },
                )
            }
        }
    }
    }
}
